import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ComplaintFormData, FormState, FormErrors, ApiResponse } from '../types';
import { validateRequired, validateEmail, validateMaxLength } from '../utils';

const initialFormData: ComplaintFormData = {
  ettevote: '',
  epost: '',
  probleem: '',
  tellimus: '',
  dokument: null
};

const initialFormState: FormState = {
  data: initialFormData,
  errors: {},
  isSubmitting: false,
  submitStatus: '',
  submitStatusType: ''
};

export const useContactForm = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormState>(initialFormState);

  const validateForm = useCallback((data: ComplaintFormData): FormErrors => {
    const errors: FormErrors = {};

    if (!validateRequired(data.ettevote)) {
      errors.ettevote = t('validation.required');
    } else if (!validateMaxLength(data.ettevote, 254)) {
      errors.ettevote = t('validation.maxLength');
    }

    if (!validateRequired(data.epost)) {
      errors.epost = t('validation.required');
    } else if (!validateEmail(data.epost)) {
      errors.epost = t('validation.email');
    } else if (!validateMaxLength(data.epost, 254)) {
      errors.epost = t('validation.maxLength');
    }

    if (!validateRequired(data.probleem)) {
      errors.probleem = t('validation.required');
    } else if (data.probleem.trim().length < 10) {
      errors.probleem = t('validation.minLength');
    }

    if (!validateRequired(data.tellimus)) {
      errors.tellimus = t('validation.required');
    } else if (!validateMaxLength(data.tellimus, 254)) {
      errors.tellimus = t('validation.maxLength');
    }

    if (!data.dokument || data.dokument.length === 0) {
      errors.dokument = t('validation.required');
    } else {
      const files = Array.from(data.dokument);
      
      // Validate file count (max 10 files)
      if (files.length > 10) {
        errors.dokument = t('validation.fileCount');
        return errors;
      }

      // Validate file types
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
      
      if (invalidFiles.length > 0) {
        errors.dokument = t('validation.fileType');
        return errors;
      }

      // Validate total file size (max 50MB combined)
      const totalSize = files.reduce((total, file) => total + file.size, 0);
      const maxTotalSize = 50 * 1024 * 1024; // 50MB in bytes
      
      if (totalSize > maxTotalSize) {
        errors.dokument = t('validation.fileSize');
        return errors;
      }
    }

    return errors;
  }, [t]);

  const updateField = useCallback((name: keyof ComplaintFormData, value: string | FileList | null) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: value
      },
      errors: {
        ...prev.errors,
        [name]: undefined // Clear field error when user starts typing
      },
      submitStatus: '', // Clear any status message when user starts typing
      submitStatusType: ''
    }));
  }, []);

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    setFormState(prev => ({
      ...prev,
      isSubmitting
    }));
  }, []);

  const setSubmitStatus = useCallback((status: string, type: 'success' | 'error' | '' = '') => {
    setFormState(prev => ({
      ...prev,
      submitStatus: status,
      submitStatusType: type
    }));
  }, []);

  const setErrors = useCallback((errors: FormErrors) => {
    setFormState(prev => ({
      ...prev,
      errors
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
  }, []);

  const handleSubmit = useCallback(async (
    onSubmit: (data: ComplaintFormData, token: string) => Promise<ApiResponse>,
    token: string
  ) => {
    const errors = validateForm(formState.data);
    
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return false;
    }

    setSubmitting(true);
    setSubmitStatus('', '');
    // Clear any previous general errors
    setErrors({ ...formState.errors, general: undefined });

    try {
      const response = await onSubmit(formState.data, token);
      if (response.success) {
        // Use translated success message instead of API message
        const successMessage = t('messages.success');
        // Reset form data but keep the success message
        setFormState({
          data: initialFormData,
          errors: {},
          isSubmitting: false,
          submitStatus: successMessage,
          submitStatusType: 'success'
        });
        return true;
      } else {
        // Handle different error types with appropriate user feedback
        const errorMessage = response.message;
        const errorCode = response.error;
        
        // For certain errors, show as general form error instead of status message
        const showAsGeneralError = errorCode && [
          'API_URL_NOT_CONFIGURED',
          'FILE_PROCESSING_ERROR',
          'HTTP_400',
          'HTTP_401',
          'HTTP_403'
        ].includes(errorCode);

        if (showAsGeneralError) {
          setErrors({
            ...formState.errors,
            general: errorMessage
          });
          setSubmitStatus('', '');
        } else {
          setSubmitStatus(errorMessage, 'error');
        }
        
        // Log error for debugging
        console.error('Form submission failed:', { errorCode, errorMessage });
        
        return false;
      }
    } catch (error) {
      console.error('Unexpected error during form submission:', error);
      
      // Handle unexpected errors (shouldn't happen with improved API error handling)
      const errorMessage = 'Ootamatu viga vormi saatmisel. Palun proovige hiljem uuesti.';
      setErrors({
        ...formState.errors,
        general: errorMessage
      });
      setSubmitStatus('', '');
      
      return false;
    } finally {
      // Always ensure loading state is cleared
      setSubmitting(false);
    }
  }, [formState.data, formState.errors, validateForm, setSubmitting, setSubmitStatus, setErrors, t]);

  return {
    formState,
    updateField,
    setSubmitting,
    setSubmitStatus,
    setErrors,
    resetForm,
    handleSubmit,
    validateForm
  };
};
